/**
 * 封装 Form Item，用于快速开发填写字段
 * @author georgemo
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import React, { Children, cloneElement, useCallback, useEffect, useMemo, useRef } from 'react';
import classNames from 'classnames';
export default function createField(useContainer, FieldWrapperComponent, options) {
    return function Field(props) {
        var _this = this;
        var _a = props.showError, showError = _a === void 0 ? true : _a, field = props.field, rule = props.rule, children = props.children, pureChildren = props.pureChildren, className = props.className, _b = props.valuePropKey, valuePropKey = _b === void 0 ? 'value' : _b, _c = props.trigger, trigger = _c === void 0 ? 'onChange' : _c, required = props.required, onFieldChange = props.onChange, addItem = props.addItem;
        var ref = useRef();
        var _d = useContainer(), values = _d.values, setValues = _d.setValues, setInitialValues = _d.setInitialValues, removeError = _d.removeError, errors = _d.errors, setRules = _d.setRules, removeRule = _d.removeRule, validateWithOption = _d.validateWithOption, setValidateValues = _d.setValidateValues, touchedState = _d.touchedState, setTouchedState = _d.setTouchedState;
        var isDelRulesWhenDestroy = (props === null || props === void 0 ? void 0 : props.isDelRulesWhenDestroy) !== undefined
            ? props === null || props === void 0 ? void 0 : props.isDelRulesWhenDestroy
            : (options === null || options === void 0 ? void 0 : options.isDelRulesWhenDestroy) !== undefined
                ? options === null || options === void 0 ? void 0 : options.isDelRulesWhenDestroy
                : true;
        // 对 FieldWrapperComponent 组件类型重新定义，以能接受更多props
        var FormItem = FieldWrapperComponent;
        // 当前这个 field 的错误
        var error = errors[field];
        // 当前这个 field 的值
        var value = 'value' in props ? props.value : values[field];
        var initialValue = 'initialValue' in props ? props.initialValue : undefined;
        // 如果 required 有传，就1⃣以 required 为准，如果不传就会取 rule
        var isFieldRequired = required === undefined
            ? (Array.isArray(rule) && rule.length > 0)
                ? rule[0].required
                : Array.isArray(rule)
                    ? false
                    : (rule && rule.required)
            : !!required;
        // 设置初始值
        useEffect(function () {
            var _a;
            if (initialValue && 'initialValue' in props) {
                setInitialValues((_a = {}, _a[field] = initialValue, _a));
            }
        }, [initialValue, field]);
        useEffect(function () {
            setValidateValues(field, value);
        }, [value]);
        useEffect(function () {
            addItem(field, ref);
            // 当 Field 对应的 touchedState 未初始化时，将其设置为 false
            if (field && touchedState[field] === undefined) {
                setTouchedState(field, false);
            }
        }, []);
        useEffect(function () {
            var _rule = rule;
            // 兼容 rule 没有 message 的默认情况
            var defaultMessage = '不能为空';
            var requiredRule = { required: !!required, message: defaultMessage };
            if (Array.isArray(rule) && required) {
                _rule = rule.map(function (r) { return (__assign({}, r)); });
                _rule.push(requiredRule);
            }
            else if (rule && required) {
                _rule = [requiredRule, __assign({}, rule)];
            }
            else if (required) {
                _rule = requiredRule;
            }
            setRules(field, _rule);
        }, [rule, required]);
        // 当当前 formItem 注销时，对应的 rule, value, error 都从 containers 中注销
        useEffect(function () { return function () {
            if (isDelRulesWhenDestroy) {
                removeRule(field);
                removeError(field);
            }
        }; }, []);
        // 统一的 onChange
        var onChange = useCallback(function (e) { return __awaiter(_this, void 0, void 0, function () {
            var value;
            var _a;
            return __generator(this, function (_b) {
                // Field 发生修改
                if (field) {
                    setTouchedState(field, true);
                }
                value = (e && e.target) ? e.target.value : e;
                // 执行 校验
                validateWithOption({
                    fieldsValue: (_a = {}, _a[field] = value, _a),
                    callback: function (errors) {
                        if (!errors) {
                            removeError(field);
                        }
                    },
                    isResetErr: false,
                });
                // 传递值
                if (onFieldChange) {
                    onFieldChange(value);
                }
                else {
                    setValues(field, value);
                }
                return [2 /*return*/];
            });
        }); }, [validateWithOption, field, value, onFieldChange]);
        // 代理的子元素
        var realChildren = useMemo(function () {
            var _a;
            if (pureChildren) {
                return children;
            }
            // 代理子元素
            if (typeof children === 'function') {
                return children({ value: value, onChange: onChange });
            }
            // 只能有一个子元素
            Children.only(children);
            return cloneElement(children, (_a = {}, _a[valuePropKey] = value, _a[trigger] = onChange, _a));
        }, [value, onChange, pureChildren, children]);
        // 不需要将某些 props 传给 Form.Item 组件，否则会透传到 DOM 上引起报错
        var excludePropsKeys = ['addItem', 'pureChildren', 'rule', 'isDelRulesWhenDestroy'];
        var formItemProps = {};
        for (var key in props) {
            if (!excludePropsKeys.includes(key)) {
                formItemProps[key] = props[key];
            }
        }
        return (React.createElement("span", { ref: ref },
            React.createElement(FormItem, __assign({ error: showError && error ? error : undefined, validateStatus: error && showError && 'error' }, formItemProps, { required: isFieldRequired, className: classNames(className, 'meta-form-field'), onChange: function () { } }), realChildren)));
    };
}
